import React from "react";
import { Formik } from "formik";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Skeleton from "@mui/material/Skeleton";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Paper from "@mui/material/Paper";
import { CaptureProvider } from "../../../contexts/capture";
import GroupedAvailableQuantityField from "../../../components/modals/InventoryTransactionMultipleModal/quantityCalculation/GroupedAvailableQuantityField";
import ImagesSlider from "../../capture/Images/ImagesSlider";
import ImageDND from "../../capture/Images/ImageDND";
import useAuth from "../../../hooks/useAuth";
import labels from "../../../constants/displayFields";
import { sortImages } from "../../../utils/images";
import ItemId from "./ItemId";

export const fetchGroupedAssetValues = asset => {
  if (!asset?.id) return {};
  return {
    manufacturer: asset.manufacturer || "",
    barcode: asset.barcode || "",
    description: asset.description || "",
    descriptionComments: asset.descriptionComments || "",
    color: asset.color || "",
    model: asset.model || "",
    skuNumber: asset.skuNumber || "",
    serializedGroupedAsset: asset.serializedGroupedAsset || false,
    dimensions: asset.dimensions,
  };
};

export default function GroupedAssetDetailsModal({
  asset,
  loading = false,
  onClose,
  readOnlyQuantityCalculation = false,
}) {
  const { user } = useAuth();
  const { settings } = user.company;
  const imageList = sortImages(asset?.images || []);
  const [imageIndex, setImageIndex] = React.useState(0);

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      onClose={onClose}
      disableEscapeKeyDown={false}
      BackdropProps={{
        onClick: onClose,
      }}
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "1000px",
          width: "100%",
        },
      }}
    >
      <DialogTitle sx={{ background: "#f4f6f8", display: "flex", justifyContent: "space-between" }}>
        <Box width="48px" />
        <Typography fontSize={24} fontWeight={500} color="textPrimary">
          Grouped Item Details
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ background: "#f4f6f8" }}>
        {loading ? (
          <Box display="flex" gap={3} p={3}>
            <Box sx={{ minWidth: 300, maxWidth: 400 }}>
              <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 2 }} />
            </Box>

            <Box flex={1} display="flex" flexDirection="column" gap={3}>
              <Box>
                <Skeleton variant="text" width={120} height={30} sx={{ mb: 2 }} />
                <Box display="flex" gap={2} mb={2}>
                  <Skeleton
                    variant="rectangular"
                    width="48%"
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="48%"
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
                <Box display="flex" gap={2}>
                  <Skeleton
                    variant="rectangular"
                    width="48%"
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width="48%"
                    height={56}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
              </Box>

              <Box>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={200}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Box display="flex" flexGrow={1}>
            <CaptureProvider>
              <Formik key={asset?.id} initialValues={fetchGroupedAssetValues(asset)}>
                <Box width="100%">
                  {/* Grouped Item Info Section */}
                  <Card>
                    <CardContent>
                      <Box mt={-1} mb={1}>
                        <Typography fontWeight={500} fontSize={18}>
                          Grouped Item Info
                        </Typography>
                      </Box>

                      <Grid container alignItems="flex-start" spacing={1}>
                        <Grid container spacing={1} size={{ md: 6, xs: 12 }}>
                          <Grid size={12}>
                            <Box
                              position="relative"
                              sx={{
                                width: { xs: "90%", sm: "50%", md: "100%" },
                                margin: "0 auto",
                              }}
                            >
                              {imageList.length === 0 && (
                                <ImageDND onDrop={() => {}} helperText="No images available" />
                              )}
                              <ImagesSlider
                                list={imageList}
                                index={imageIndex}
                                onChangeIndex={setImageIndex}
                              />
                            </Box>
                          </Grid>

                          <Grid size={12}>
                            <Button
                              sx={{ height: 40, borderWidth: 2, "&:hover": { borderWidth: 2 } }}
                              variant="outlined"
                              color="primary"
                              fullWidth
                              disabled
                            >
                              <AddAPhotoIcon />
                            </Button>
                          </Grid>
                        </Grid>

                        <Grid container spacing={1} size={{ md: 6, xs: 12 }}>
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Base Warehouse"
                              value={
                                asset.warehousesFromRackLocations?.[0]?.name ||
                                asset.project?.warehouses?.[0]?.name ||
                                ""
                              }
                              disabled
                              sx={{
                                fieldset: { borderColor: "primary.main" },
                                label: { color: "primary.main" },
                                input: { color: "primary.main" },
                              }}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              fullWidth
                              size="small"
                              label={settings.filter_field2}
                              value={asset.project?.name || ""}
                              disabled
                              sx={{
                                fieldset: { borderColor: "primary.main" },
                                label: { color: "primary.main" },
                                input: { color: "primary.main" },
                              }}
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              size="small"
                              label="SKU ID"
                              disabled
                              value={asset.itemId}
                              fullWidth
                            />
                          </Grid>

                          <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                              size="small"
                              label="SKU Number"
                              disabled
                              value={asset.skuNumber || ""}
                              fullWidth
                            />
                          </Grid>

                          <Grid size={12}>
                            <TextField
                              size="small"
                              fullWidth
                              label={labels.description}
                              disabled
                              value={asset.description || ""}
                              multiline
                              rows={2}
                            />
                          </Grid>

                          <Grid size={12}>
                            <GroupedAvailableQuantityField
                              asset={asset}
                              hideCalculationInfoButtons={readOnlyQuantityCalculation}
                            />
                          </Grid>

                          <Grid size={12}>
                            <TextField
                              size="small"
                              label="Quantity in Warehouse"
                              disabled
                              value={asset.groupedMemberAssets?.total_available_quantity || 0}
                              fullWidth
                            />
                          </Grid>

                          <Grid size={12}>
                            <Box>
                              <Box
                                sx={{
                                  px: 1,
                                  display: "flex",
                                  justifyContent: "center",
                                  height: 36.5,
                                  borderRadius: "4px",
                                  border: "1px solid rgba(0, 0, 0, 0.23)",
                                  "& label": {
                                    display: "flex",
                                    flexDirection: "row-reverse",
                                    justifyContent: "center",
                                    mx: 0,
                                    height: 36.5,
                                    "& > span > p": {
                                      lineHeight: 1,
                                      color: "text.primary",
                                    },
                                  },
                                }}
                              >
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={asset.serializedGroupedAsset || false}
                                      disabled
                                      sx={{ p: 1 }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" color="textPrimary">
                                      Serialized Grouped Item
                                    </Typography>
                                  }
                                />
                              </Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                sx={{ mt: 0.5, display: "block" }}
                              >
                                When enabled, warehouse will manually assign specific serialized
                                assets during fulfillment. When disabled, system will auto-assign
                                using FIFO at checkout.
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Reference Item Section */}
                  {asset.groupedMemberAssets && (
                    <Box mt={2}>
                      <Paper>
                        <Box p={2}>
                          <Box mb={2}>
                            <Typography fontWeight={500} fontSize={18}>
                              Reference Item
                            </Typography>
                          </Box>

                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">SKU Number</TableCell>
                                <TableCell align="center">Associated Item IDs</TableCell>
                                <TableCell align="center">Description</TableCell>
                                <TableCell align="center">Quantity in Warehouse</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              <TableRow>
                                <TableCell align="center">
                                  {asset.groupedMemberAssets.sku_number || "—"}
                                </TableCell>

                                <TableCell align="center">
                                  {asset.groupedMemberAssets.associated_item_ids &&
                                  asset.groupedMemberAssets.associated_item_ids.length > 0
                                    ? asset.groupedMemberAssets.associated_item_ids.map((itemId, idx) => {
                                        const associatedAssetId =
                                          asset.groupedMemberAssets.associated_asset_ids?.[idx];

                                        return (
                                          <Box key={associatedAssetId || itemId || idx}>
                                            {associatedAssetId ? (
                                              <ItemId
                                                asset={{
                                                  id: associatedAssetId,
                                                  itemId,
                                                  project: asset.project,
                                                }}
                                              />
                                            ) : (
                                              <Typography component="span" color="primary.main">
                                                {itemId}
                                              </Typography>
                                            )}
                                          </Box>
                                        );
                                      })
                                    : "—"}
                                </TableCell>

                                <TableCell align="center">
                                  {asset.groupedMemberAssets.description || "—"}
                                </TableCell>

                                <TableCell align="center">
                                  {asset.groupedMemberAssets.total_available_quantity || 0}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </Box>
                      </Paper>
                    </Box>
                  )}
                </Box>
              </Formik>
            </CaptureProvider>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
